import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { resolveImage } from "@/lib/siteDefaults";
import { useCart } from "@/context/CartContext";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { ArrowLeft, Check, BellRing, Crown } from "lucide-react";
import { SHOP } from "@/constants/testIds";
import { toast } from "sonner";
import WishlistHeart from "@/components/WishlistHeart";
import ProductReviewsSection from "@/components/ProductReviewsSection";
import { EarlyAccessGate } from "@/components/community/EarlyAccess";
import { usePriveProductIds } from "@/lib/community";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [restockEmail, setRestockEmail] = useState("");
  const [restockSubmitting, setRestockSubmitting] = useState(false);
  const [restockSubscribed, setRestockSubscribed] = useState(false);
  const { add, items } = useCart();
  const { user } = useCustomerAuth();
  const priveIds = usePriveProductIds();

  useEffect(() => {
    setLoading(true);
    api
      .get(`/products/${id}`)
      .then((r) => {
        setProduct(r.data);
        // Auto-select first size that isn't sold out
        const sizes = r.data.sizes || [];
        const sbs = r.data.stock_by_size;
        if (sizes.length > 0) {
          const firstAvailable = sizes.find((s) => {
            if (!sbs || !(s in sbs)) return true; // untracked = available
            return sbs[s] > 0;
          });
          setSize(firstAvailable || sizes[0]);
        }
        if ((r.data.colors || []).length > 0) setColor(r.data.colors[0]);
        setError(false);
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, [id]);

  // Prefill restock email from logged-in customer
  useEffect(() => {
    if (user?.email) setRestockEmail(user.email);
  }, [user]);

  // Reset subscribed state when size changes so form re-appears for a different sold-out size
  useEffect(() => {
    setRestockSubscribed(false);
  }, [size, id]);

  const submitRestock = async (e) => {
    e?.preventDefault?.();
    if (!product) return;
    const email = restockEmail.trim();
    if (!email || !email.includes("@")) {
      toast.error("Enter a valid email");
      return;
    }
    setRestockSubmitting(true);
    try {
      await api.post("/restock/notify", {
        email,
        product_id: product.id,
        size: size || null,
      });
      setRestockSubscribed(true);
      toast.success("We'll email you the moment it's back.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't sign you up");
    } finally {
      setRestockSubmitting(false);
    }
  };

  // Effective stock for the currently selected size (null = untracked/unlimited)
  const effectiveStock = (() => {
    if (!product) return null;
    const sbs = product.stock_by_size;
    if (sbs && Object.keys(sbs).length > 0) {
      if (size && size in sbs) return Number(sbs[size]);
      return null; // sized product but this size isn't tracked
    }
    return product.stock == null ? null : Number(product.stock);
  })();

  const onAdd = () => {
    if (!product) return;
    if ((product.sizes || []).length > 0 && !size) {
      toast.error("Please choose a size");
      return;
    }
    if (effectiveStock != null && effectiveStock <= 0) {
      toast.error(size ? `Size ${size} is sold out` : "Sold out");
      return;
    }
    // How many of this product+variant are already in the cart?
    const alreadyInCart = (items || [])
      .filter((i) => i.product_id === product.id && (i.size || null) === (size || null) && (i.color || null) === (color || null))
      .reduce((s, i) => s + i.quantity, 0);
    if (effectiveStock != null && alreadyInCart + 1 > effectiveStock) {
      const label = size ? `${size} of ${product.name}` : product.name;
      toast.error(`Only ${effectiveStock} of ${label} available — you already have ${alreadyInCart} in your cart.`);
      return;
    }
    add(product, { size, color, effectiveStock });
    toast.success(`${product.name} added to cart`);
  };

  if (loading) {
    return (
      <div className="pt-32 max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 grid md:grid-cols-2 gap-10 min-h-screen">
        <div className="aspect-[4/5] bg-[#F4F1EA] rounded-2xl animate-pulse" />
        <div className="space-y-5 mt-8">
          <div className="h-4 w-20 bg-[#F4F1EA] rounded animate-pulse" />
          <div className="h-12 w-3/4 bg-[#F4F1EA] rounded animate-pulse" />
          <div className="h-24 w-full bg-[#F4F1EA] rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="pt-40 pb-40 text-center">
        <p className="font-serif text-4xl italic text-stone-600">Product not found</p>
        <Link to="/shop" className="mt-6 inline-block text-sm underline">Back to shop</Link>
      </div>
    );
  }

  const images = product.images?.length ? product.images : [];

  return (
    <div className="pt-24 md:pt-28 pb-24 md:pb-32">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-2 text-sm text-stone-500 hover:text-[#1A1C1A] mb-8 transition-colors"
        >
          <ArrowLeft strokeWidth={1.5} className="w-4 h-4" /> Back
        </button>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16 items-start">
          <div>
            <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-[#F4F1EA] relative">
              {images[activeImg] && (
                <img src={resolveImage(images[activeImg])} alt={product.name} className="w-full h-full object-cover" />
              )}
              <div className="absolute top-4 right-4">
                <WishlistHeart productId={product.id} size="lg" />
              </div>
            </div>
            {images.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3">
                {images.map((img, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveImg(i)}
                    className={`aspect-square rounded-lg overflow-hidden border-2 ${
                      i === activeImg ? "border-[#1A1C1A]" : "border-transparent"
                    }`}
                  >
                    <img src={resolveImage(img)} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="md:sticky md:top-28">
            <p className="text-xs uppercase tracking-[0.3em] text-stone-500 font-semibold mb-4">
              {product.category === "gym" ? "Performance" : "Renewal Beauty"}
            </p>
            <h1
              data-testid={SHOP.productTitle}
              className="font-serif text-4xl md:text-5xl lg:text-6xl font-light tracking-tighter text-[#1A1C1A] leading-[1]"
            >
              {product.name}
            </h1>
            <p data-testid={SHOP.productPrice} className="mt-6 font-sans text-2xl text-[#1A1C1A]">
              ${product.price.toFixed(2)} <span className="text-sm text-stone-500 uppercase tracking-wider ml-1">USD</span>
            </p>

            <p data-testid={SHOP.productDescription} className="mt-8 text-base leading-relaxed text-stone-600 font-light max-w-md">
              {product.description}
            </p>

            {(product.sizes || []).length > 0 && (
              <div className="mt-10">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3">Size</p>
                <div className="flex flex-wrap gap-2" data-testid={SHOP.sizeSelector}>
                  {product.sizes.map((s) => {
                    const sbs = product.stock_by_size;
                    const sizeStock = sbs && s in sbs ? Number(sbs[s]) : null;
                    const soldOutSize = sizeStock != null && sizeStock <= 0;
                    const isSelected = size === s;
                    return (
                      <button
                        key={s}
                        onClick={() => setSize(s)}
                        title={soldOutSize ? "Sold out — tap to get notified when back" : (sizeStock != null && sizeStock <= 3 ? `Only ${sizeStock} left` : undefined)}
                        className={`relative w-12 h-12 rounded-full border font-sans text-sm transition-all ${
                          soldOutSize
                            ? isSelected
                              ? "bg-[#F4F1EA] border-[#8B3A3A] text-[#8B3A3A] line-through"
                              : "bg-transparent border-stone-200 text-stone-400 line-through hover:border-stone-400"
                            : isSelected
                            ? "bg-[#1A1C1A] text-[#F9F9F6] border-[#1A1C1A]"
                            : "bg-transparent border-stone-300 text-[#1A1C1A] hover:border-[#1A1C1A]"
                        }`}
                        data-testid={`size-option-${s}`}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {(product.colors || []).length > 0 && (
              <div className="mt-8">
                <p className="text-xs uppercase tracking-[0.2em] text-stone-500 font-semibold mb-3">
                  Color · <span className="text-[#1A1C1A]">{color}</span>
                </p>
                <div className="flex flex-wrap gap-2" data-testid={SHOP.colorSelector}>
                  {product.colors.map((c) => (
                    <button
                      key={c}
                      onClick={() => setColor(c)}
                      className={`px-4 h-10 rounded-full border font-sans text-sm transition-all ${
                        color === c
                          ? "bg-[#1A1C1A] text-[#F9F9F6] border-[#1A1C1A]"
                          : "bg-transparent border-stone-300 text-[#1A1C1A] hover:border-[#1A1C1A]"
                      }`}
                      data-testid={`color-option-${c}`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {effectiveStock != null && effectiveStock <= 0 ? (
              <div className="mt-8">
                <p className="text-xs uppercase tracking-[0.2em] text-[#8B3A3A] font-semibold mb-4">
                  {size ? `Size ${size} sold out` : "Sold out — check back soon"}
                </p>
                {restockSubscribed ? (
                  <div
                    data-testid="restock-subscribed"
                    className="flex items-center gap-2 px-4 py-3 rounded-full bg-[#EAE7DC] text-[#3F5F3F] text-sm max-w-md"
                  >
                    <Check strokeWidth={1.5} className="w-4 h-4" />
                    <span>You're on the list. We'll email you the moment it's back{size ? ` in ${size}` : ""}.</span>
                  </div>
                ) : (
                  <form
                    onSubmit={submitRestock}
                    data-testid="restock-form"
                    className="flex flex-col sm:flex-row gap-2 max-w-md"
                  >
                    <input
                      type="email"
                      required
                      value={restockEmail}
                      onChange={(e) => setRestockEmail(e.target.value)}
                      placeholder="your@email.com"
                      data-testid="restock-email-input"
                      className="flex-1 px-4 py-3 rounded-full border border-stone-300 bg-white text-sm text-[#1A1C1A] placeholder:text-stone-400 focus:outline-none focus:border-[#1A1C1A]"
                    />
                    <button
                      type="submit"
                      disabled={restockSubmitting}
                      data-testid="restock-submit-button"
                      className="inline-flex items-center justify-center gap-2 bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-6 py-3 rounded-full transition-all duration-300 font-sans text-xs tracking-wider uppercase disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
                    >
                      <BellRing strokeWidth={1.5} className="w-3.5 h-3.5" />
                      {restockSubmitting ? "Signing up…" : "Notify me"}
                    </button>
                  </form>
                )}
              </div>
            ) : (
              <>
                {effectiveStock != null && effectiveStock <= 5 && (
                  <p className="mt-8 text-xs uppercase tracking-[0.2em] text-[#8B6A1A] font-semibold">
                    Only {effectiveStock} left{size ? ` in ${size}` : ""}
                  </p>
                )}
                <EarlyAccessGate productId={product.id}>
                <button
                  data-testid={SHOP.addToCartButton}
                  onClick={onAdd}
                  className="mt-4 w-full md:w-auto bg-[#1A1C1A] text-[#F9F9F6] hover:bg-[#3C3E3C] px-12 py-4 rounded-full transition-all duration-300 font-sans text-sm tracking-wider uppercase"
                >
                  Add to cart — ${product.price.toFixed(2)}
                </button>
                </EarlyAccessGate>
              </>
            )}
            {priveIds?.includes(product.id) && (
              <Link to="/prive" data-testid="product-prive-eligible-note" className="mt-6 flex items-start gap-2 text-sm text-[#8B6A3A] bg-[#FBF5EE] border border-[#E8D9C4] rounded-xl px-4 py-3 hover:bg-[#F7EDE2] transition-colors">
                <Crown strokeWidth={1.5} className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Purchasing this item makes you eligible to join the Jenova Privé channel and the Jenova Girls community.</span>
              </Link>
            )}

            <div className="mt-10 pt-8 border-t border-stone-200 space-y-3 text-sm text-stone-600 font-light">
              <p className="flex items-center gap-2"><Check strokeWidth={1.5} className="w-4 h-4" /> Free shipping on orders over $75</p>
              <p className="flex items-center gap-2"><Check strokeWidth={1.5} className="w-4 h-4" /> 30-day returns</p>
              <p className="flex items-center gap-2"><Check strokeWidth={1.5} className="w-4 h-4" /> Ethically produced in small batches</p>
            </div>
          </div>
        </div>
        <ProductReviewsSection productId={product.id} productName={product.name} />
      </div>
    </div>
  );
}
